import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardHeader, CardTitle, CardContent } from './ui/card';
import { Button } from './ui/button';
import {
  Building, DollarSign, Home, Wrench, AlertCircle, Clock,
  Loader2, MessageSquare, FileText, CreditCard, ChevronRight,
  MapPin, CalendarDays, Bot, Sparkles, CheckSquare, Circle,
  CheckCircle2, Phone, Mail, RotateCcw, StickyNote, Zap
} from 'lucide-react';
import { propertyService } from '../services/propertyService';
import { tenantService } from '../services/tenantService';
import { leaseService } from '../services/leaseService';
import maintenanceService from '../services/maintenanceService';
import { chatService } from '../services/chatService';
import { activityService } from '../services/activityService';
import { organizationService } from '../services/organizationService';
import { billingService } from '../services/billingService';
import NivaOnboardingTour from './NivaOnboardingTour';

const Dashboard = ({ user }) => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  // Landlord state
  const [properties, setProperties] = useState([]);
  const [tenants, setTenants] = useState([]);
  const [leases, setLeases] = useState([]);
  const [maintenanceCases, setMaintenanceCases] = useState([]);
  const [upcomingTasks, setUpcomingTasks] = useState([]);

  // Tenant state
  const [chatSessions, setChatSessions] = useState([]);
  const [tenantProfile, setTenantProfile] = useState(null);

  const isLandlord = user?.userType === 'Landlord';

  useEffect(() => {
    fetchDashboardData();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchDashboardData = async () => {
    try {
      setLoading(true);
      setError('');

      if (isLandlord) {
        const [propsData, tenantsData, leasesData, maintData, tasksData] = await Promise.all([
          propertyService.getAll(),
          tenantService.getAllTenants(),
          leaseService.getAll(),
          maintenanceService.getAll(),
          activityService.getUpcomingTasks().catch(() => []),
        ]);
        setProperties(propsData);
        setTenants(tenantsData);
        setLeases(leasesData);
        setMaintenanceCases(maintData);
        setUpcomingTasks(tasksData);
      } else {
        const [maintData, sessionsRes, profileRes] = await Promise.all([
          maintenanceService.getAll(),
          chatService.getChatSessions().catch(() => ({ sessions: [] })),
          tenantService.getMe().catch(() => null),
        ]);
        setMaintenanceCases(maintData);
        setChatSessions(Array.isArray(sessionsRes) ? sessionsRes : sessionsRes?.sessions || []);
        setTenantProfile(profileRes);
      }
    } catch (err) {
      console.error('Failed to fetch dashboard data:', err);
      setError('Failed to load dashboard data');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <AlertCircle className="h-8 w-8 text-red-500 mx-auto mb-2" />
          <p className="text-muted-foreground">{error}</p>
          <Button variant="outline" className="mt-4" onClick={fetchDashboardData}>
            Retry
          </Button>
        </div>
      </div>
    );
  }

  if (isLandlord) {
    return <LandlordDashboard
      properties={properties}
      tenants={tenants}
      leases={leases}
      maintenanceCases={maintenanceCases}
      upcomingTasks={upcomingTasks}
      navigate={navigate}
    />;
  }

  return (
    <>
      <NivaOnboardingTour user={user} />
      <TenantDashboard
        maintenanceCases={maintenanceCases}
        chatSessions={chatSessions}
        tenantProfile={tenantProfile}
        navigate={navigate}
      />
    </>
  );
};

// ─── Plan Usage Banner ────────────────────────────────────────────

const PLAN_LABELS = { free: 'Free', starter: 'Starter', professional: 'Professional', portfolio: 'Portfolio' };

const PlanBanner = () => {
  const [usage, setUsage] = React.useState(null);
  const [upgrading, setUpgrading] = React.useState(false);

  React.useEffect(() => {
    organizationService.getUsage().then(setUsage).catch(() => {});
  }, []);

  if (!usage) return null;

  const { plan, warnings, limits } = usage;
  const atLimit = warnings.unitsAtLimit || warnings.messagesAtLimit;
  const nearLimit = !atLimit && (warnings.unitsPct >= 80 || warnings.messagesPct >= 80);
  if (!atLimit && !nearLimit) return null;

  const handleUpgrade = async () => {
    setUpgrading(true);
    const nextPlan = plan === 'free' ? 'starter' : 'professional';
    try { await billingService.startCheckout(nextPlan); }
    catch { setUpgrading(false); }
  };

  return (
    <div className={`flex items-center justify-between gap-4 px-4 py-3 rounded-lg border text-sm ${
      atLimit
        ? 'bg-red-50 border-red-200 text-red-800'
        : 'bg-amber-50 border-amber-200 text-amber-800'
    }`}>
      <div className="flex items-center gap-2">
        <Zap className="h-4 w-4 flex-shrink-0" />
        <span>
          {atLimit
            ? `You've reached a limit on your ${PLAN_LABELS[plan]} plan.`
            : `You're approaching a limit on your ${PLAN_LABELS[plan]} plan.`}
          {warnings.unitsAtLimit && ` Properties: ${usage.usage.unitsUsed}/${limits.maxUnits}.`}
          {warnings.messagesAtLimit && ` Niva messages: ${usage.usage.messagesThisMonth.toLocaleString()}/${limits.nivaMsgsPerMonth?.toLocaleString()}.`}
        </span>
      </div>
      {plan !== 'portfolio' && (
        <button
          onClick={handleUpgrade}
          disabled={upgrading}
          className="flex-shrink-0 text-xs font-semibold underline hover:no-underline disabled:opacity-50"
        >
          {upgrading ? 'Redirecting…' : 'Upgrade plan →'}
        </button>
      )}
    </div>
  );
};

// ─── Landlord Dashboard ────────────────────────────────────────────

const TASK_KIND_ICONS = {
  task:      CheckSquare,
  follow_up: RotateCcw,
  call:      Phone,
  email:     Mail,
  note:      StickyNote,
  visit:     MapPin,
};

const LandlordDashboard = ({ properties, tenants, leases, maintenanceCases, upcomingTasks = [], navigate }) => {
  const totalProperties = properties.length;
  const expectedRent = properties.reduce((sum, p) => sum + (p.monthlyRent || 0), 0);
  const totalUnits = properties.reduce((sum, p) => sum + (p.units || 0), 0);
  const activeTenants = tenants.filter(t => t.status === 'Active').length;
  const vacantUnits = Math.max(0, totalUnits - activeTenants);

  const openStatuses = ['Open', 'In Progress', 'Pending Parts', 'Scheduled', 'Pending Approval'];
  const openMaintenance = maintenanceCases.filter(m => openStatuses.includes(m.status));

  const pendingApprovals = maintenanceCases.filter(m => m.approvalStatus === 'pending');

  const now = new Date();
  const thirtyDaysFromNow = new Date(now.getTime() + 30 * 24 * 60 * 60 * 1000);
  const expiringLeases = leases.filter(l => {
    if (l.status !== 'Active') return false;
    const end = new Date(l.leaseEnd);
    return end >= now && end <= thirtyDaysFromNow;
  });

  const recentMaintenance = [...maintenanceCases]
    .sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt))
    .slice(0, 5);

  return (
    <div className="space-y-6">
      <h2 className="text-3xl font-bold tracking-tight">Dashboard</h2>

      <PlanBanner />

      {/* Summary Cards */}
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Total Properties</CardTitle>
            <Building className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{totalProperties}</div>
            <p className="text-xs text-muted-foreground">{totalUnits} total units</p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Expected Monthly Rent</CardTitle>
            <DollarSign className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">${expectedRent.toLocaleString()}</div>
            <p className="text-xs text-muted-foreground">Across all properties</p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Vacant Units</CardTitle>
            <Home className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{vacantUnits}</div>
            <p className="text-xs text-muted-foreground">of {totalUnits} total units</p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Open Maintenance</CardTitle>
            <Wrench className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{openMaintenance.length}</div>
            <p className="text-xs text-muted-foreground">{pendingApprovals.length} pending approval</p>
          </CardContent>
        </Card>
      </div>

      {/* Properties + My Tasks — side by side */}
      <div className="grid gap-4 md:grid-cols-2">
        {/* Properties */}
        <Card>
          <CardHeader className="flex flex-row items-center justify-between">
            <CardTitle>Properties</CardTitle>
            <Button variant="outline" size="sm" onClick={() => navigate('/properties')}>
              View All <ChevronRight className="ml-1 h-4 w-4" />
            </Button>
          </CardHeader>
          <CardContent>
            {properties.length === 0 ? (
              <p className="text-sm text-muted-foreground text-center py-4">
                No properties yet. Add your first property to get started.
              </p>
            ) : (
              <div className="space-y-2">
                {properties.slice(0, 5).map(property => (
                  <div key={property._id} className="flex items-center justify-between py-2 border-b last:border-0">
                    <div className="min-w-0 flex-1">
                      <p className="font-medium text-sm truncate">{property.name}</p>
                      <p className="text-xs text-muted-foreground truncate">{property.address}</p>
                    </div>
                    <div className="text-right text-sm flex-shrink-0 ml-2">
                      <p>{property.units} units</p>
                      <p className="text-muted-foreground">${(property.monthlyRent || 0).toLocaleString()}/mo</p>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* My Tasks */}
        <Card>
          <CardHeader className="flex flex-row items-center justify-between">
            <CardTitle className="flex items-center gap-2">
              <CheckSquare className="h-5 w-5 text-amber-500" />
              My Tasks
            </CardTitle>
            <Button variant="outline" size="sm" onClick={() => navigate('/tenants')}>
              Add in Tenants <ChevronRight className="ml-1 h-4 w-4" />
            </Button>
          </CardHeader>
          <CardContent>
            {upcomingTasks.length === 0 ? (
              <p className="text-sm text-muted-foreground text-center py-4">
                No open tasks. Open a tenant's activity feed to add one.
              </p>
            ) : (
              <div className="space-y-0">
                {upcomingTasks.map(task => {
                  const now = new Date();
                  const due = task.dueDate ? new Date(task.dueDate) : null;
                  const overdue = due && due < now;
                  const dueToday = due && due >= now && due <= new Date(now.getFullYear(), now.getMonth(), now.getDate(), 23, 59, 59);
                  const Icon = TASK_KIND_ICONS[task.kind] || CheckSquare;
                  return (
                    <div key={task._id} className="flex items-start gap-3 py-2.5 border-b last:border-0">
                      <div className={`flex-shrink-0 h-6 w-6 rounded-full flex items-center justify-center mt-0.5 ${overdue ? 'bg-red-100' : dueToday ? 'bg-amber-100' : 'bg-gray-100'}`}>
                        <Icon className={`h-3 w-3 ${overdue ? 'text-red-600' : dueToday ? 'text-amber-600' : 'text-gray-500'}`} />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium truncate">{task.title}</p>
                        <div className="flex items-center gap-2 mt-0.5 flex-wrap">
                          {task.tenant && (
                            <span className="text-xs text-muted-foreground">{task.tenant.name}</span>
                          )}
                          {due && (
                            <span className={`text-xs font-medium ${overdue ? 'text-red-600' : dueToday ? 'text-amber-600' : 'text-muted-foreground'}`}>
                              {overdue ? 'Overdue · ' : dueToday ? 'Due today · ' : ''}
                              {due.toLocaleDateString(undefined, { month: 'short', day: 'numeric' })}
                            </span>
                          )}
                          {task.priority === 'High' && (
                            <span className="text-[10px] font-semibold text-red-600 bg-red-50 border border-red-200 px-1.5 py-0.5 rounded">High</span>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Action Items & Recent Maintenance */}
      <div className="grid gap-4 md:grid-cols-2">
        {/* Action Items */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <AlertCircle className="h-5 w-5 text-orange-500" />
              Action Items
            </CardTitle>
          </CardHeader>
          <CardContent>
            {pendingApprovals.length === 0 && expiringLeases.length === 0 ? (
              <p className="text-sm text-muted-foreground text-center py-4">No action items right now.</p>
            ) : (
              <div className="space-y-3">
                {pendingApprovals.map(m => (
                  <div key={m._id} className="flex items-start gap-3 py-2 border-b last:border-0">
                    <Wrench className="h-4 w-4 mt-0.5 text-orange-500" />
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium truncate">
                        Approval needed: {m.title}
                      </p>
                      <p className="text-xs text-muted-foreground">
                        {m.caseNumber} &middot; {m.priority} priority
                      </p>
                    </div>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => navigate(`/maintenance/approval/${m._id}`)}
                    >
                      Review
                    </Button>
                  </div>
                ))}
                {expiringLeases.map(l => (
                  <div key={l._id} className="flex items-start gap-3 py-2 border-b last:border-0">
                    <Clock className="h-4 w-4 mt-0.5 text-yellow-500" />
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium truncate">
                        Lease expiring: {l.tenant?.name || 'Unknown tenant'}
                      </p>
                      <p className="text-xs text-muted-foreground">
                        {l.property?.name} &middot; Ends {new Date(l.leaseEnd).toLocaleDateString()}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Recent Maintenance */}
        <Card>
          <CardHeader className="flex flex-row items-center justify-between">
            <CardTitle>Recent Maintenance</CardTitle>
            <Button variant="outline" size="sm" onClick={() => navigate('/maintenance')}>
              View All <ChevronRight className="ml-1 h-4 w-4" />
            </Button>
          </CardHeader>
          <CardContent>
            {recentMaintenance.length === 0 ? (
              <p className="text-sm text-muted-foreground text-center py-4">No maintenance cases yet.</p>
            ) : (
              <div className="space-y-2">
                {recentMaintenance.map(m => (
                  <div key={m._id} className="flex items-center justify-between py-2 border-b last:border-0">
                    <div className="min-w-0 flex-1">
                      <p className="text-sm font-medium truncate">{m.title}</p>
                      <p className="text-xs text-muted-foreground">
                        {m.caseNumber} &middot; {new Date(m.createdAt).toLocaleDateString()}
                      </p>
                    </div>
                    <div className="flex items-center gap-2 ml-2">
                      <StatusBadge status={m.status} />
                      <PriorityBadge priority={m.priority} />
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>

    </div>
  );
};

// ─── Tenant Dashboard ──────────────────────────────────────────────

const TenantDashboard = ({ maintenanceCases, chatSessions, tenantProfile, navigate }) => {
  const property = tenantProfile?.property;
  const [achStatus, setAchStatus] = React.useState(null);
  const [outstanding, setOutstanding] = React.useState(null);

  React.useEffect(() => {
    import('../services/paymentService').then(({ paymentService }) => {
      Promise.allSettled([
        paymentService.getMyAchStatus(),
        paymentService.getMyLedger(),
      ]).then(([statusRes, ledgerRes]) => {
        if (statusRes.status === 'fulfilled') setAchStatus(statusRes.value);
        if (ledgerRes.status === 'fulfilled') {
          const bal = ledgerRes.value?.currentBalance || 0;
          setOutstanding(bal > 0 ? bal : 0);
        }
      });
    });
  }, []);

  const formatDate = (d) => d ? new Date(d).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' }) : 'N/A';

  // Helper: navigate to Niva with a pre-filled prompt
  const openNiva = (prompt) => navigate('/niva', { state: { nivaPrompt: prompt } });

  // Lease expiry awareness
  const now = new Date();
  const daysUntilLeaseEnd = tenantProfile?.leaseEnd
    ? Math.ceil((new Date(tenantProfile.leaseEnd) - now) / (1000 * 60 * 60 * 24))
    : null;
  const leaseExpiringSoon = daysUntilLeaseEnd !== null && daysUntilLeaseEnd > 0 && daysUntilLeaseEnd <= 60;

  const openCases = maintenanceCases.filter(m =>
    ['Open', 'In Progress', 'Pending Parts', 'Scheduled', 'Pending Approval'].includes(m.status)
  );

  return (
    <div className="space-y-6">
      <h2 className="text-3xl font-bold tracking-tight">Dashboard</h2>

      {/* ── My Property ──────────────────────────────────────────── */}
      {tenantProfile && (
        <Card className="border-2 border-primary/10 bg-gradient-to-br from-white to-primary/5">
          <CardContent className="pt-6">
            <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
              {/* Property info */}
              <div className="flex gap-4 min-w-0">
                <div className="flex-shrink-0 bg-primary/10 rounded-xl p-3 self-start">
                  <Home className="h-6 w-6 text-primary" />
                </div>
                <div className="min-w-0">
                  <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide mb-0.5">My Property</p>
                  <h3 className="text-xl font-bold truncate">{property?.name || 'Your Property'}</h3>
                  <div className="flex items-start gap-1.5 mt-1.5">
                    <MapPin className="h-4 w-4 text-muted-foreground flex-shrink-0 mt-0.5" />
                    <p className="text-sm text-muted-foreground">{property?.address || 'Address on file'}</p>
                  </div>
                  <div className="flex flex-wrap gap-4 mt-3">
                    <div>
                      <p className="text-xs text-muted-foreground">Unit</p>
                      <p className="text-sm font-semibold">{tenantProfile.unitNumber}</p>
                    </div>
                    <div>
                      <p className="text-xs text-muted-foreground">Lease Period</p>
                      <div className="flex items-center gap-1.5">
                        <CalendarDays className="h-3.5 w-3.5 text-muted-foreground" />
                        <p className="text-sm font-semibold">
                          {formatDate(tenantProfile.leaseStart)} &mdash; {formatDate(tenantProfile.leaseEnd)}
                        </p>
                      </div>
                    </div>
                    <div>
                      <p className="text-xs text-muted-foreground">Status</p>
                      <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium mt-0.5 ${
                        tenantProfile.status === 'Active'
                          ? 'bg-green-100 text-green-800'
                          : 'bg-gray-100 text-gray-800'
                      }`}>
                        {tenantProfile.status}
                      </span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Niva CTA */}
              <div className="flex-shrink-0">
                <Button
                  size="lg"
                  className="gap-2 w-full sm:w-auto"
                  onClick={() => navigate('/niva')}
                >
                  <Bot className="h-5 w-5" />
                  Chat with Niva
                </Button>
                <p className="text-xs text-muted-foreground text-center mt-1.5">
                  Report issues &middot; Check status
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Lease expiry Niva CTA */}
      {leaseExpiringSoon && (
        <div className="flex items-center justify-between gap-4 bg-amber-50 border border-amber-200 rounded-xl px-4 py-3">
          <div className="flex items-center gap-3 min-w-0">
            <div className="flex-shrink-0 bg-amber-100 rounded-lg p-2">
              <CalendarDays className="h-4 w-4 text-amber-600" />
            </div>
            <div className="min-w-0">
              <p className="text-sm font-semibold text-amber-900">
                Your lease ends in {daysUntilLeaseEnd} day{daysUntilLeaseEnd !== 1 ? 's' : ''}
              </p>
              <p className="text-xs text-amber-700">Ask Niva about your renewal options before it lapses.</p>
            </div>
          </div>
          <Button
            size="sm"
            className="flex-shrink-0 bg-amber-500 hover:bg-amber-600 text-white gap-1.5"
            onClick={() => openNiva('I have questions about my lease renewal.')}
          >
            <Sparkles className="h-3.5 w-3.5" />
            Ask Niva
          </Button>
        </div>
      )}

      {/* No profile fallback CTA */}
      {!tenantProfile && (
        <Card className="border-dashed">
          <CardContent className="flex flex-col sm:flex-row items-center justify-between gap-4 py-6">
            <div className="flex items-center gap-3">
              <Bot className="h-8 w-8 text-primary" />
              <div>
                <p className="font-semibold">Need help with something?</p>
                <p className="text-sm text-muted-foreground">Chat with Niva to report maintenance issues or check request status.</p>
              </div>
            </div>
            <Button
              size="lg"
              className="gap-2 flex-shrink-0"
              onClick={() => navigate('/niva')}
            >
              <MessageSquare className="h-4 w-4" />
              Chat with Niva
            </Button>
          </CardContent>
        </Card>
      )}

      <div className="grid gap-4 md:grid-cols-2">
        {/* Active Maintenance Requests */}
        <Card className="md:col-span-2">
          <CardHeader className="flex flex-row items-center justify-between">
            <CardTitle className="flex items-center gap-2">
              <Wrench className="h-5 w-5" />
              My Maintenance Requests
            </CardTitle>
            <div className="flex items-center gap-2">
              {openCases.length > 0 && (
                <Button
                  size="sm"
                  variant="ghost"
                  className="gap-1.5 text-cyan-600 hover:text-cyan-700 hover:bg-cyan-50 text-xs"
                  onClick={() => openNiva('Can you check the status of my maintenance requests?')}
                >
                  <Sparkles className="h-3.5 w-3.5" />
                  Ask Niva for updates
                </Button>
              )}
              <Button variant="outline" size="sm" onClick={() => navigate('/maintenance')}>
                View All <ChevronRight className="ml-1 h-4 w-4" />
              </Button>
            </div>
          </CardHeader>
          <CardContent>
            {maintenanceCases.length === 0 ? (
              <div className="text-center py-6">
                <Wrench className="h-8 w-8 text-muted-foreground/30 mx-auto mb-3" />
                <p className="text-sm text-muted-foreground mb-3">No maintenance requests yet.</p>
                <Button
                  size="sm"
                  className="bg-gradient-to-r from-cyan-600 to-blue-600 hover:from-cyan-700 hover:to-blue-700 gap-1.5"
                  onClick={() => openNiva('I need to report a maintenance issue.')}
                >
                  <Sparkles className="h-3.5 w-3.5" />
                  Report an issue with Niva
                </Button>
              </div>
            ) : (
              <div className="space-y-0">
                {maintenanceCases.map(m => {
                  const isOpen = ['Open', 'In Progress', 'Pending Parts', 'Scheduled', 'Pending Approval'].includes(m.status);
                  return (
                    <div key={m._id} className="flex items-center justify-between py-2.5 border-b last:border-0 group">
                      <div className="min-w-0 flex-1">
                        <p className="text-sm font-medium truncate">{m.title}</p>
                        <p className="text-xs text-muted-foreground">
                          {m.caseNumber} &middot; {new Date(m.createdAt).toLocaleDateString()}
                        </p>
                      </div>
                      <div className="flex items-center gap-2 ml-2">
                        <StatusBadge status={m.status} />
                        <PriorityBadge priority={m.priority} />
                        {isOpen && (
                          <button
                            onClick={() => openNiva(`Can you check the status of my maintenance request ${m.caseNumber}?`)}
                            className="opacity-0 group-hover:opacity-100 transition-opacity flex items-center gap-1 text-[11px] text-cyan-600 hover:text-cyan-700 font-medium px-1.5 py-0.5 rounded-md hover:bg-cyan-50"
                            title="Ask Niva for a status update"
                          >
                            <Sparkles className="h-3 w-3" />
                            Ask Niva
                          </button>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Message History */}
        <Card>
          <CardHeader className="flex flex-row items-center justify-between">
            <CardTitle className="flex items-center gap-2">
              <MessageSquare className="h-5 w-5" />
              Message History
            </CardTitle>
            <Button variant="outline" size="sm" onClick={() => navigate('/messages')}>
              View All <ChevronRight className="ml-1 h-4 w-4" />
            </Button>
          </CardHeader>
          <CardContent>
            {chatSessions.length === 0 ? (
              <p className="text-sm text-muted-foreground text-center py-4">No chat sessions yet.</p>
            ) : (
              <div className="space-y-1">
                {chatSessions.slice(0, 5).map((session, idx) => (
                  <button
                    key={session.sessionId || session._id || idx}
                    onClick={() => navigate(`/messages?session=${session.sessionId}`)}
                    className="w-full text-left py-2 px-2 -mx-2 rounded-md border-b last:border-0 hover:bg-gray-50 transition-colors group"
                  >
                    <p className="text-sm font-medium truncate group-hover:text-primary">
                      {session.title || `Chat session ${idx + 1}`}
                    </p>
                    <p className="text-xs text-muted-foreground">
                      {session.lastActivity
                        ? new Date(session.lastActivity).toLocaleDateString()
                        : session.updatedAt
                          ? new Date(session.updatedAt).toLocaleDateString()
                          : 'No activity'
                      }
                      {session.messageCount > 0 && ` · ${session.messageCount} messages`}
                    </p>
                  </button>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Payments Card */}
        <Card>
          <CardHeader className="flex flex-row items-center justify-between">
            <CardTitle className="flex items-center gap-2">
              <CreditCard className="h-5 w-5" />
              Payments
            </CardTitle>
            <Button variant="outline" size="sm" onClick={() => navigate('/payments')}>
              View <ChevronRight className="ml-1 h-4 w-4" />
            </Button>
          </CardHeader>
          <CardContent>
            {!achStatus ? (
              <div className="flex items-center justify-center py-4">
                <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
              </div>
            ) : achStatus.achStatus !== 'verified' ? (
              <div className="flex flex-col items-center justify-center py-4 text-center">
                <CreditCard className="h-8 w-8 text-muted-foreground/30 mb-2" />
                <p className="text-sm text-muted-foreground mb-3">Connect your bank to enable automatic rent payments.</p>
                <Button size="sm" className="bg-cyan-600 hover:bg-cyan-700 text-white" onClick={() => navigate('/payments')}>
                  Set Up Payments
                </Button>
              </div>
            ) : achStatus.subscriptionActive ? (
              <div className="space-y-3">
                <div className="flex justify-between items-center py-1 border-b">
                  <span className="text-sm text-muted-foreground">Outstanding Balance</span>
                  <span className={`text-sm font-semibold ${outstanding > 0 ? 'text-red-600' : 'text-green-600'}`}>
                    ${(outstanding || 0).toLocaleString()}
                  </span>
                </div>
                {achStatus.bankLast4 && (
                  <div className="flex justify-between items-center py-1 border-b">
                    <span className="text-sm text-muted-foreground">Payment Method</span>
                    <span className="text-sm font-medium">
                      {achStatus.bankName || 'Bank'} ••••{achStatus.bankLast4}
                    </span>
                  </div>
                )}
                {achStatus.paymentDay && (
                  <div className="flex justify-between items-center py-1">
                    <span className="text-sm text-muted-foreground">Billing Day</span>
                    <span className="text-sm font-medium">Day {achStatus.paymentDay}</span>
                  </div>
                )}
              </div>
            ) : (
              <div className="flex flex-col items-center justify-center py-4 text-center">
                <CheckCircle2 className="h-8 w-8 text-amber-400 mb-2" />
                <p className="text-sm text-muted-foreground">
                  Bank connected. Waiting for your landlord to activate payments.
                </p>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Shared Documents Placeholder */}
        <Card className="md:col-span-2">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <FileText className="h-5 w-5" />
              Shared Documents
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex flex-col items-center justify-center py-6 text-center">
              <FileText className="h-10 w-10 text-muted-foreground/30 mb-3" />
              <p className="text-sm font-medium text-muted-foreground">Coming Soon</p>
              <p className="text-xs text-muted-foreground mt-1">
                Document sharing between tenants and landlords will be available in a future update.
              </p>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

// ─── Helper Components ─────────────────────────────────────────────

const StatusBadge = ({ status }) => {
  const colors = {
    'Open': 'bg-blue-100 text-blue-800',
    'In Progress': 'bg-yellow-100 text-yellow-800',
    'Pending Parts': 'bg-orange-100 text-orange-800',
    'Scheduled': 'bg-purple-100 text-purple-800',
    'Pending Approval': 'bg-orange-100 text-orange-800',
    'Resolved': 'bg-green-100 text-green-800',
    'Closed': 'bg-gray-100 text-gray-800',
  };
  return (
    <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${colors[status] || 'bg-gray-100 text-gray-800'}`}>
      {status}
    </span>
  );
};

const PriorityBadge = ({ priority }) => {
  const colors = {
    'Emergency': 'bg-red-100 text-red-800',
    'High': 'bg-orange-100 text-orange-800',
    'Medium': 'bg-yellow-100 text-yellow-800',
    'Low': 'bg-green-100 text-green-800',
  };
  return (
    <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${colors[priority] || 'bg-gray-100 text-gray-800'}`}>
      {priority}
    </span>
  );
};

export default Dashboard;
